import { FetchResult, gql, MutationFunctionOptions, useMutation, useQuery } from '@apollo/client'
import { t } from '@lingui/macro'

import { Toast } from '@/components'
import { Events } from '@/constants'
import { AddFavoritePeerPayload, Maybe, Query, RemoveFavoritePeerPayload, User } from '@/graphql/generated'

import useTracking from '../useTracking'

type ReturnType = {
  loading: boolean
  mutationRunning: boolean
  userProfile: Maybe<User | undefined>
  removeFavoritePeer: (
    options?: MutationFunctionOptions<RemoveFavoritePeerPayload> | undefined,
  ) => Promise<FetchResult<void>>
  addFavoritePeer: (options?: MutationFunctionOptions<AddFavoritePeerPayload> | undefined) => Promise<FetchResult<void>>
}
const useCommunityProfile = (userId: string, cohortId: string | undefined): ReturnType => {
  const { trackEvent, withCurrentLearningCommunity, withCurrentCohortId } = useTracking()

  const { data, loading } = useQuery<Pick<Query, 'user'>>(GET_USER_PROFILE, {
    skip: !userId || !cohortId,
    variables: { userId, cohortId },
  })

  const sendTrackingEvent = (eventName: string): void => {
    trackEvent(eventName, withCurrentLearningCommunity(), withCurrentCohortId())
  }

  const updatedCohortUserProfileFragment = gql`
    fragment UpdatedCohortUserProfile on CohortUserProfile {
      viewer {
        __typename
        favorite
      }
    }
  `

  const [removeFavoritePeer, { loading: isRemovingFavoritePeer }] = useMutation(REMOVE_FAVORITE_PEER, {
    update: (cache, { data }, { variables }) => {
      if (data?.errors) return

      cache.modify({
        id: cache.identify({
          __typename: 'User',
          id: variables?.userId,
        }),
        fields: {
          cohortProfile: (existingProfile, { readField }) => {
            const updatedProfileRef = cache.writeFragment({
              id: cache.identify({
                __typename: 'CohortUserProfile',
                id: readField('id', existingProfile),
              }),
              data: {
                viewer: {
                  __typename: 'CohortUserProfileViewer',
                  favorite: false,
                },
              },
              fragment: updatedCohortUserProfileFragment,
            })
            return updatedProfileRef
          },
        },
      })
    },
    onCompleted: data => {
      if (data.removeFavoritePeer.errors) {
        Toast({
          message: t`Something went wrong. Try again later.`,
          type: 'error',
        })
      }

      sendTrackingEvent(Events.REMOVED_FAVORITE_PEER)
    },
  })

  const [addFavoritePeer, { loading: isAddingFavoritePeer }] = useMutation(ADD_FAVORITE_PEER, {
    update: (cache, { data }, { variables }) => {
      if (data?.errors) return

      cache.modify({
        id: cache.identify({
          __typename: 'User',
          id: variables?.userId,
        }),
        fields: {
          cohortProfile: (existingProfile, { readField }) => {
            const updatedProfileRef = cache.writeFragment({
              id: cache.identify({
                __typename: 'CohortUserProfile',
                id: readField('id', existingProfile),
              }),
              data: {
                viewer: {
                  __typename: 'CohortUserProfileViewer',
                  favorite: true,
                },
              },
              fragment: updatedCohortUserProfileFragment,
            })
            return updatedProfileRef
          },
        },
      })
    },
    onCompleted: data => {
      if (data.addFavoritePeer.errors) {
        Toast({
          message: t`Something went wrong. Try again later.`,
          type: 'error',
        })
      }

      sendTrackingEvent(Events.FAVORITED_PEER)
    },
  })

  return {
    userProfile: data?.user,
    loading,
    mutationRunning: isRemovingFavoritePeer || isAddingFavoritePeer,
    removeFavoritePeer,
    addFavoritePeer,
  }
}

export const REMOVE_FAVORITE_PEER = gql`
  mutation RemoveFavoritePeer($userId: ID!, $cohortId: ID!) {
    removeFavoritePeer(userId: $userId, cohortId: $cohortId) {
      success
      errors {
        field
        message
        extensions {
          code
          model
        }
        path
      }
    }
  }
`

export const ADD_FAVORITE_PEER = gql`
  mutation AddFavoritePeer($userId: ID!, $cohortId: ID!) {
    addFavoritePeer(userId: $userId, cohortId: $cohortId) {
      success
      errors {
        field
        message
        extensions {
          code
          model
        }
        path
      }
    }
  }
`

export const GET_USER_PROFILE = gql`
  query GetUserProfile($userId: ID!, $cohortId: ID!) {
    user(userId: $userId) {
      id
      firstName
      lastName
      email
      aboutMe
      fullName
      avatarUrl
      jobDescription
      pronouns
      professionalUrl
      location
      timezone
      cohortProfile(cohortId: $cohortId) {
        id
        canBeHelpedWith
        canHelpWith
        enrollmentReason
        viewer {
          favorite
        }
      }
      application(cohortId: $cohortId) {
        id
        invoiceLink
      }
    }
  }
`

export default useCommunityProfile
